import { useState, useCallback } from 'react';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';

interface UsePlacementPhotosOptions {
  orderId: string;
}

export function usePlacementPhotos({ orderId }: UsePlacementPhotosOptions) {
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [uploadingFileStates, setUploadingFileStates] = useState<
    Map<string, 'uploading' | 'success' | 'error'>
  >(new Map());
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  const generateSignedUrlMutation = trpc.admin.placementPhoto.generateSignedUploadUrl.useMutation();
  const finalizeUploadMutation = trpc.admin.placementPhoto.finalizeUpload.useMutation();
  const deletePhotoMutation = trpc.admin.placementPhoto.deletePhoto.useMutation();

  const isUploading = uploadingFiles.length > 0;

  const uploadFileToSignedUrl = async (
    file: File,
    signedUrl: string,
    contentType: string,
  ): Promise<void> => {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Failed to upload file: ${response.statusText}`);
    }
  };

  const uploadFiles = useCallback(
    async (files: File[]) => {
      setUploadingFiles((prev) => [...prev, ...files]);

      for (const file of files) {
        setUploadingFileStates((prev) => new Map(prev).set(file.name, 'uploading'));

        try {
          // Step 1: Get signed URL
          const signedUrlResult = await generateSignedUrlMutation.mutateAsync({
            orderId,
            fileName: file.name,
            contentType: file.type,
            size: file.size,
          });

          // Step 2: Upload to GCS
          await uploadFileToSignedUrl(file, signedUrlResult.signedUrl, file.type);

          // Step 3: Finalize
          const photo = await finalizeUploadMutation.mutateAsync({
            orderId,
            fileName: signedUrlResult.fileName,
            contentType: file.type,
            size: file.size,
          });

          // Patch the photo into the cached order data so we don't need to refetch the entire order
          utils.admin.order.adminGetOrderRelatedInfoById.setData(orderId, (prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              order: {
                ...prev.order,
                placementPhotos: [...(prev.order.placementPhotos || []), photo],
              },
            };
          });

          setUploadingFileStates((prev) => new Map(prev).set(file.name, 'success'));
          setUploadingFiles((prev) => prev.filter((f) => f.name !== file.name));
          setUploadingFileStates((prev) => {
            const next = new Map(prev);
            next.delete(file.name);
            return next;
          });
        } catch (error) {
          setUploadingFileStates((prev) => new Map(prev).set(file.name, 'error'));
          errorToast({
            title: 'Upload failed',
            description: `Failed to upload ${file.name}`,
          });

          // Remove failed file after a delay
          setTimeout(() => {
            setUploadingFiles((prev) => prev.filter((f) => f.name !== file.name));
            setUploadingFileStates((prev) => {
              const next = new Map(prev);
              next.delete(file.name);
              return next;
            });
          }, 3000);
        }
      }
    },
    [orderId, generateSignedUrlMutation, finalizeUploadMutation, utils, errorToast],
  );

  const deletePhoto = useCallback(
    async (fileName: string) => {
      try {
        await deletePhotoMutation.mutateAsync({ orderId, fileName });
        utils.admin.order.adminGetOrderRelatedInfoById.setData(orderId, (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            order: {
              ...prev.order,
              placementPhotos: (prev.order.placementPhotos || []).filter(
                (p) => p.fileName !== fileName,
              ),
            },
          };
        });
        successToast({ title: 'Photo deleted' });
      } catch (error) {
        errorToast({
          title: 'Delete failed',
          description: 'Failed to delete the photo',
        });
      }
    },
    [orderId, deletePhotoMutation, utils, successToast, errorToast],
  );

  return {
    uploadFiles,
    deletePhoto,
    isUploading,
    uploadingFiles,
    uploadingFileStates,
    isDeletingPhoto: deletePhotoMutation.isPending,
  };
}
